import React, { type ReactNode, useRef } from 'react';
import {
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaStreetNumberProps } from '../../../fields/mla-street-number/mla-street-number/mla-street-number-props';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  findChildOfType,
  applyInternalProps,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type StreetNumberProps = {
  children: ReactNode;
};

export const StreetNumber = ({ children }: StreetNumberProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<MlaStreetNumberProps>();

  const streetNumberLabel = useAddressLabel('streetNumber');
  const labelElement = findChildOfType(children, StreetNumber.Label);
  const inputElement = findChildOfType(children, StreetNumber.Input);
  const descriptionElement = findChildOfType(
    children,
    StreetNumber.Description,
  );
  const errorElement = findChildOfType(children, StreetNumber.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: streetNumberLabel,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id } = useFieldPropsV2<MlaStreetNumberProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    />
  );
};

StreetNumber.Label = Label;
StreetNumber.Input = Input;
StreetNumber.Description = Description;
StreetNumber.Error = Error;
